import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';
import { Plus, Edit, Trash2, LogOut, Video as VideoIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const AdminDashboard = ({ onLogout }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    youtubeUrl: ''
  });

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/videos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideos(response.data);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    try {
      if (editingVideo) {
        // Update existing video
        await axios.put(`${API}/videos/${editingVideo.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: 'Video updated successfully!' });
      } else {
        // Add new video
        await axios.post(`${API}/videos`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: 'Video added successfully!' });
      }
      
      setShowAddDialog(false);
      setEditingVideo(null);
      setFormData({ title: '', description: '', youtubeUrl: '' });
      fetchVideos();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to save video',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      description: video.description,
      youtubeUrl: video.youtubeUrl
    });
    setShowAddDialog(true);
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/videos/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Video deleted successfully!' });
      fetchVideos();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete video',
        variant: 'destructive'
      });
    }
  };

  const extractYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-700 via-blue-700 to-purple-700 bg-clip-text text-transparent">
              Video Management
            </h1>
            <p className="text-slate-600 mt-1">Manage instructional videos</p>
          </div>
          <Button
            onClick={onLogout}
            variant="outline"
            className="border-red-300 text-red-600 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Quick Instructions Card */}
        <Card className="mb-6 border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50">
          <CardContent className="py-4">
            <div className="flex items-start gap-4">
              <div className="bg-purple-100 rounded-full p-2">
                <VideoIcon className="w-6 h-6 text-purple-700" />
              </div>
              <div>
                <h3 className="font-semibold text-purple-900 mb-1">Quick Guide: Adding Videos</h3>
                <p className="text-sm text-slate-600">
                  Click <span className="font-semibold text-purple-700">"Add New Video"</span> → 
                  Paste your YouTube link → Add a title and description → Click <span className="font-semibold text-purple-700">"Add Video"</span>
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Videos will automatically appear on the public "Instructional Videos" page with thumbnails.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Add Video Button */}
        <Button
          onClick={() => {
            setEditingVideo(null);
            setFormData({ title: '', description: '', youtubeUrl: '' });
            setShowAddDialog(true);
          }}
          className="mb-6 bg-purple-700 hover:bg-purple-800 h-12 px-6 text-base"
          data-testid="add-new-video-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add New Video
        </Button>

        {/* Videos List */}
        {loading ? (
          <p className="text-center text-slate-600">Loading videos...</p>
        ) : videos.length === 0 ? (
          <Card className="border-2 border-slate-200">
            <CardContent className="py-12 text-center">
              <VideoIcon className="w-16 h-16 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600 text-lg">No videos yet</p>
              <p className="text-slate-500 text-sm mt-2">Click "Add New Video" to get started</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {videos.map((video) => {
              const youtubeId = extractYouTubeId(video.youtubeUrl);
              return (
                <Card key={video.id} className="border-2 border-slate-200">
                  <div className="relative">
                    {youtubeId ? (
                      <img
                        src={`https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`}
                        alt={video.title}
                        className="w-full h-48 object-cover rounded-t-lg"
                        onError={(e) => {
                          e.target.src = `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
                        }}
                      />
                    ) : (
                      <div className="w-full h-48 bg-slate-200 flex items-center justify-center rounded-t-lg">
                        <VideoIcon className="w-12 h-12 text-slate-400" />
                      </div>
                    )}
                  </div>
                  <CardContent className="pt-4">
                    <h3 className="font-semibold text-lg text-slate-900 mb-2">
                      {video.title}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                      {video.description}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleEdit(video)}
                        size="sm"
                        variant="outline"
                        className="flex-1"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        onClick={() => handleDelete(video.id)}
                        size="sm"
                        variant="outline"
                        className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingVideo ? 'Edit Video' : 'Add New Video'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* YouTube URL Instructions Box */}
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                <p className="text-sm font-semibold text-blue-900 mb-2">How to Add a YouTube Link:</p>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Go to your YouTube video</li>
                  <li>Copy the URL from your browser's address bar</li>
                  <li>Paste it in the field below</li>
                </ol>
                <p className="text-xs text-blue-600 mt-2">
                  Example: https://www.youtube.com/watch?v=dQw4w9WgXcQ
                </p>
              </div>
              
              <div>
                <Label htmlFor="youtubeUrl" className="text-base font-semibold">YouTube URL *</Label>
                <Input
                  id="youtubeUrl"
                  type="url"
                  value={formData.youtubeUrl}
                  onChange={(e) => setFormData({ ...formData, youtubeUrl: e.target.value })}
                  placeholder="Paste your YouTube link here..."
                  required
                  className="mt-1 text-base h-12"
                  data-testid="youtube-url-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Accepts: youtube.com/watch?v=... or youtu.be/... links
                </p>
              </div>
              <div>
                <Label htmlFor="title" className="text-base font-semibold">Video Title *</Label>
                <Input
                  id="title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Introduction to the Reclaimation Process"
                  required
                  className="mt-1"
                  data-testid="video-title-input"
                />
              </div>
              <div>
                <Label htmlFor="description" className="text-base font-semibold">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe what viewers will learn from this video..."
                  rows={4}
                  required
                  className="mt-1"
                  data-testid="video-description-input"
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddDialog(false);
                    setEditingVideo(null);
                    setFormData({ title: '', description: '', youtubeUrl: '' });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-purple-700 hover:bg-purple-800"
                >
                  {editingVideo ? 'Update Video' : 'Add Video'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminDashboard;